import { useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router'
import { ArrowLeft, Save, Trash2, Phone, PhoneCall, Mail, FileText } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useContact, useUpdateContact, useDeleteContact } from '../api/contacts'
import { formatPhone, formatDateTime } from '../lib/utils'
import { SpinLoader } from '../components/shared/SpinLoader'
import { ErrorCard } from '../components/shared/ErrorCard'
import { CallStagingBriefing } from '../components/contact/CallStagingBriefing'
import { PhoneCallHistory } from '../components/phone/PhoneCallHistory'

export function ContactDetailPage() {
  const { t } = useTranslation('contacts')
  const { contactId } = useParams()
  const id = contactId ? Number(contactId) : undefined
  const navigate = useNavigate()
  const { data, isLoading, isError, refetch } = useContact(id)
  const updateContact = useUpdateContact()
  const deleteContact = useDeleteContact()

  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState({ name: '', email: '', notes: '' })
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const startEditing = () => {
    if (data?.contact) {
      setForm({
        name: data.contact.name,
        email: data.contact.email,
        notes: data.contact.notes,
      })
      setEditing(true)
    }
  }

  const handleSave = async () => {
    if (id == null) return
    await updateContact.mutateAsync({ id, ...form })
    setEditing(false)
  }

  const handleDelete = async () => {
    if (id == null) return
    await deleteContact.mutateAsync(id)
    navigate('/app/contacts')
  }

  if (isLoading) return <SpinLoader label={t('detail.loading')} />

  if (isError || !data) {
    return (
      <div className="space-y-4">
        <Link to="/app/contacts" className="inline-flex items-center gap-1 text-sm text-text-secondary hover:text-primary">
          <ArrowLeft className="h-4 w-4" />
          {t('detail.backToContacts')}
        </Link>
        <ErrorCard message={t('detail.notFound')} onRetry={() => refetch()} />
      </div>
    )
  }

  const { contact } = data

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3 md:gap-4">
        <Link
          to="/app/contacts"
          className="flex items-center gap-1.5 text-sm text-text-secondary hover:text-primary transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('detail.backToContacts')}
        </Link>
        <h1 className="text-xl font-bold text-text-primary md:text-2xl">
          {contact.name || formatPhone(contact.phone)}
        </h1>
      </div>

      {/* Profile Card */}
      <div className="rounded-lg border border-border bg-surface-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <h2 className="text-lg font-semibold text-text-primary">{t('detail.profile')}</h2>
          <div className="flex gap-2">
            {!editing ? (
              <>
                <button
                  onClick={() => navigate(`/app/calls/stage/${encodeURIComponent(contact.phone)}?contactId=${contact.id}`)}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-success px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-success/80"
                >
                  <PhoneCall className="h-4 w-4" />
                  {t('detail.callButton')}
                </button>
                <button
                  onClick={startEditing}
                  className="rounded-lg border border-border px-3 py-1.5 text-sm text-text-secondary transition-colors hover:bg-surface-elevated"
                >
                  {t('detail.edit')}
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  aria-label={t('detail.delete')}
                  className="rounded-lg border border-error/30 px-3 py-1.5 text-sm text-error transition-colors hover:bg-error/10"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleSave}
                  disabled={updateContact.isPending}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-50"
                >
                  <Save className="h-4 w-4" />
                  {updateContact.isPending ? t('detail.saving') : t('detail.save')}
                </button>
                <button
                  onClick={() => setEditing(false)}
                  className="rounded-lg border border-border px-3 py-1.5 text-sm text-text-secondary transition-colors hover:bg-surface-elevated"
                >
                  {t('detail.cancel')}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Delete Confirmation */}
        {showDeleteConfirm && (
          <div className="mt-3 rounded-lg border border-error/30 bg-error/5 p-3">
            <p className="text-sm text-text-primary">
              {t('detail.deleteConfirm')}
            </p>
            <div className="mt-2 flex gap-2">
              <button
                onClick={handleDelete}
                disabled={deleteContact.isPending}
                className="rounded-lg bg-error px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-error/90 disabled:opacity-50"
              >
                {deleteContact.isPending ? t('detail.deleting') : t('detail.deleteConfirmYes')}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="rounded-lg border border-border px-3 py-1.5 text-sm text-text-secondary transition-colors hover:bg-surface-elevated"
              >
                {t('detail.cancel')}
              </button>
            </div>
          </div>
        )}

        {/* Fields */}
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field icon={Phone} label={t('detail.fields.phone')} value={formatPhone(contact.phone)} />
          {editing ? (
            <>
              <EditField
                label={t('detail.fields.name')}
                value={form.name}
                onChange={(v) => setForm((f) => ({ ...f, name: v }))}
              />
              <EditField
                label={t('detail.fields.email')}
                value={form.email}
                onChange={(v) => setForm((f) => ({ ...f, email: v }))}
                type="email"
              />
              <EditField
                label={t('detail.fields.notes')}
                value={form.notes}
                onChange={(v) => setForm((f) => ({ ...f, notes: v }))}
                multiline
              />
            </>
          ) : (
            <>
              <Field label={t('detail.fields.name')} value={contact.name} placeholder={t('detail.placeholders.noName')} />
              <Field icon={Mail} label={t('detail.fields.email')} value={contact.email} placeholder={t('detail.placeholders.noEmail')} />
              <Field
                icon={FileText}
                label={t('detail.fields.notes')}
                value={contact.notes}
                placeholder={t('detail.placeholders.noNotes')}
                className="sm:col-span-2"
              />
            </>
          )}
        </div>

        <div className="mt-4 flex gap-4 text-xs text-text-muted">
          <span>{t('detail.meta.created', { value: formatDateTime(contact.created_at) })}</span>
          <span>{t('detail.meta.updated', { value: formatDateTime(contact.updated_at) })}</span>
        </div>
      </div>

      {/* Briefing card — person-level facts, open commitments from prior
          calls, and last touch per purpose. Same component used on the
          staging page. */}
      <CallStagingBriefing contactId={contact.id} />

      {/* Full Call History */}
      <PhoneCallHistory phoneNumber={contact.phone} />
    </div>
  )
}

function Field({
  icon: Icon,
  label,
  value,
  placeholder,
  className = '',
}: {
  icon?: React.ComponentType<{ className?: string }>
  label: string
  value: string
  placeholder?: string
  className?: string
}) {
  return (
    <div className={className}>
      <p className="text-xs font-medium text-text-muted">{label}</p>
      <p className="mt-0.5 flex items-center gap-1.5 text-sm text-text-primary">
        {Icon && <Icon className="h-3.5 w-3.5 text-text-muted" />}
        {value || <span className="italic text-text-muted">{placeholder}</span>}
      </p>
    </div>
  )
}

function EditField({
  label,
  value,
  onChange,
  type = 'text',
  multiline = false,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  type?: string
  multiline?: boolean
}) {
  return (
    <div className={multiline ? 'sm:col-span-2' : ''}>
      <label className="text-xs font-medium text-text-muted">{label}</label>
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className="mt-0.5 w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="mt-0.5 w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        />
      )}
    </div>
  )
}
